import type { EmotionalFamily, EmotionalState } from '../../../domain/emotion/enums';
import type { DayMode } from '../../../domain/routine/enums';
import type { IconName } from '../core/Icon';

/**
 * Types partagés de la résolution de présentation ASCEND V7. Ce fichier ne contient aucune
 * donnée ni logique — seulement la forme des données portées par `modes.ts`,
 * `emotional-impact.ts`, produites par `priority-matrix.ts` et `resolvePresentation.ts`.
 */

export type Locale = 'fr-FR' | 'en-US' | 'es-MX';

export const LOCALES: readonly Locale[] = ['fr-FR', 'en-US', 'es-MX'];

/** Vocabulaire d'exigence du mode — copie validée du handoff, ne pas inventer de valeur. */
export type ModeExigence = 'minimale' | 'basse' | 'souple' | 'standard' | 'haute' | 'maximale';

/** Vocabulaire de densité — copie validée du handoff. */
export type ModeDensity = 'aérée' | 'standard' | 'dense' | 'resserrée';

/** Niveau de mouvement retenu pour l'atmosphère — jamais utilisé pour masquer un élément. */
export type MovementIntensity = 'normal' | 'reduced' | 'still';

/**
 * Éléments d'interface pouvant être atténués (jamais retirés) par un état émotionnel, ou
 * retirés explicitement par une préférence utilisateur. Vocabulaire stable, pas de chaîne libre.
 */
export type PresentationElement =
  | 'progress'
  | 'editorial-cards'
  | 'priorities'
  | 'durations'
  | 'guideline-panel';

export interface ModeEditorialContent {
  readonly mantraKey: string;
  readonly mantra: string;
  readonly thoughtKey: string;
  readonly thought: string;
  readonly intentionKey: string;
  readonly intention: string;
  readonly routineKeys: readonly string[];
  readonly routines: readonly string[];
}

export interface ModePresentationDefinition {
  readonly mode: DayMode;
  /** Clé de conception ASCEND (ex. `elevation`, `moment-decisif`) — pilote scope CSS, icône, imagerie. */
  readonly key: string;
  readonly labelKey: string;
  /** Libellé de référence (fallback hors traduction, et utilisé par les tests). */
  readonly label: string;
  readonly icon: IconName;
  readonly scopeClass: string;
  readonly heroImageKey: string;
  /** Point focal CSS `object-position` de `heroImageKey` — chaque image du pack a un cadrage propre. */
  readonly heroImagePosition: string;
  readonly accent: string;
  readonly light: string;
  readonly density: ModeDensity;
  readonly exigence: ModeExigence;
  readonly tone: string;
  readonly shortDescriptionKey: string;
  readonly shortDescription: string;
  readonly functionalConsequenceKey: string;
  /** Référence le texte déjà validé de `presentation/design/modeCopy.ts` — jamais retypé ici. */
  readonly functionalConsequence: string;
  readonly content: ModeEditorialContent;
}

export interface EmotionalFamilyPresentationDefinition {
  readonly family: EmotionalFamily;
  readonly key: string;
  readonly labelKey: string;
  readonly label: string;
  readonly icon: IconName;
  readonly scopeClass: string;
  readonly heroImageKey: string;
  /** Point focal CSS `object-position` de `heroImageKey` — chaque image du pack a un cadrage propre. */
  readonly heroImagePosition: string;
  readonly accent: string;
  readonly light: string;
  readonly tone: string;
  readonly movementIntensity: MovementIntensity;
  readonly intentionKey: string;
  readonly intention: string;
  readonly thoughtKey: string;
  readonly thought: string;
  readonly routineKeys: readonly string[];
  readonly routines: readonly string[];
  /** Éléments que cette famille peut atténuer visuellement — jamais masquer. */
  readonly attenuates: readonly PresentationElement[];
}

export interface EmotionalStatePresentationDefinition {
  readonly state: EmotionalState;
  readonly labelKey: string;
  /** Libellé de référence, copie validée du handoff (majuscules d'origine conservées). */
  readonly label: string;
}

export interface PresentationPreferences {
  readonly showProgress: boolean;
  readonly showEditorialCards: boolean;
  readonly showTimes: boolean;
  readonly locale: Locale;
}

export interface AccessibilityPreferences {
  /** AGS — contraste renforcé, cibles agrandies, atténuation annulée, mouvement figé. */
  readonly ags: boolean;
  readonly reducedMotion: boolean;
}

export interface PresentationInput {
  readonly mode: DayMode;
  /** Aucun état par défaut : le mode Normal n'est pas l'état Joie. */
  readonly state?: EmotionalState | null;
  readonly prefs?: Partial<PresentationPreferences>;
  readonly a11y?: Partial<AccessibilityPreferences>;
}

/** Explique la résolution obtenue — aide au débogage et aux tests, jamais affiché brut à l'utilisateur. */
export interface ResolutionExplanation {
  readonly modeReason: string;
  readonly emotionalReason: string;
  readonly accessibilityReason: string;
  readonly preferencesReason: string;
}

export interface ResolvedPresentation {
  readonly mode: DayMode;
  readonly state: EmotionalState | null;
  readonly family: EmotionalFamily | null;
  readonly modeScopeClass: string;
  readonly emotionalScopeClass: string | null;
  /** Toutes les classes de portée à appliquer, dans l'ordre d'application (le dernier gagne). */
  readonly scopes: readonly string[];
  readonly heroImage: string;
  /** Point focal CSS `object-position` de `heroImage` — cadrage propre à chaque image du pack. */
  readonly heroImagePosition: string;
  readonly icon: IconName;
  readonly emotionalIcon: IconName | null;
  readonly accent: string;
  readonly light: string;
  readonly density: ModeDensity;
  readonly exigence: ModeExigence;
  readonly movementIntensity: MovementIntensity;
  readonly tone: string;
  readonly dimmed: readonly PresentationElement[];
  readonly emphasized: readonly PresentationElement[];
  readonly hiddenByUser: readonly PresentationElement[];
  readonly thoughtKey: string;
  readonly thought: string;
  readonly intentionKey: string;
  readonly intention: string;
  readonly routineKeys: readonly string[];
  readonly routines: readonly string[];
  readonly locale: Locale;
  readonly ags: boolean;
  readonly reducedMotion: boolean;
  readonly explanation: ResolutionExplanation;
}
